import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FaArrowRight,
    FaChevronLeft,
    FaChevronRight,
    FaLock,
    FaPaperPlane,
    FaShieldAlt,
    FaUsersCog,
} from "react-icons/fa";
import DashboardLayout from "../../../../layouts/DashboardLayout";
import { useAuth } from "../../../../context/AuthContext";
import { useSystem } from "../../../../context/SystemContext";
import CertificatePreview from "../../components/CertificatePreview/CertificatePreview";
import PrivacyNoticeModal from "../../components/PrivacyNoticeModal/PrivacyNoticeModal";
import "../../styles/Home.css";

const shortcuts = [
    {
        label: "Emisiones",
        mobileLabel: "Emisiones",
        description: "Consulta y gestiona emisiones de certificados.",
        to: "/emissions",
        privilege: "MOD_EMISSION:READ",
        icon: FaPaperPlane,
    },
    {
        label: "Validación de certificados",
        mobileLabel: "Validación",
        description: "Verifica la autenticidad de un certificado.",
        to: "/certificates-validation",
        privilege: "MOD_VALIDATION:READ",
        icon: FaShieldAlt,
    },
    {
        label: "Reportes",
        mobileLabel: "Reportes",
        description: "Consulta los reportes de certificados.",
        to: "/reports",
        privilege: "MOD_REPORTS:READ",
        icon: FaUsersCog,
    },
];

const certificatePreviews = [
    {
        type: "property",
        eyebrow: "CERTIFICADO DE",
        title: "Patrimonio y bienes inmuebles",
        folio: "PAT-••••••••",
        sections: [
            {
                title: "RESUMEN PATRIMONIAL",
                rows: [
                    ["Total predios", "1"],
                    ["Impuesto predial", "AL CORRIENTE"],
                ],
            },
            {
                title: "PREDIO URBANO",
                rows: [
                    ["Clave catastral", "QRO-•••"],
                    ["Estatus", "ACTIVO"],
                ],
            },
        ],
    },
    {
        type: "citizen",
        eyebrow: "CERTIFICADO DEL",
        title: "Expediente único ciudadano",
        folio: "EUC-••••••••",
        sections: [
            {
                title: "DATOS PERSONALES",
                rows: [
                    ["Nombre", "PERSONA CIUDADANA"],
                    ["RFC / CURP", "••••••••"],
                ],
            },
            {
                title: "TRÁMITES Y SERVICIOS",
                rows: [
                    ["Servicios", "INTEGRADOS"],
                    ["Validación", "VIGENTE"],
                ],
            },
        ],
    },
    {
        type: "business",
        eyebrow: "CERTIFICADO",
        title: "Empresarial y de actividad económica",
        folio: "EMP-••••••••",
        sections: [
            {
                title: "DATOS GENERALES",
                rows: [
                    ["Persona", "MORAL"],
                    ["RFC", "••••••••"],
                ],
            },
            {
                title: "SITUACIÓN FISCAL",
                rows: [
                    ["Opinión", "POSITIVA"],
                    ["Actividad", "REGISTRADA"],
                ],
            },
        ],
    },
];

const COMPACT_LAYOUT_QUERY = "(max-width: 900px)";

function HomeShortcuts({ user, hasPrivilege, compact = false }) {
    return (
        <section
            className={`home-shortcuts${compact ? " home-shortcuts-compact" : ""}`}
            aria-labelledby="home-shortcuts-title"
        >
            <div className="home-section-heading">
                <div>
                    <p className="home-eyebrow">Accesos</p>
                    <h2 id="home-shortcuts-title">Servicios disponibles</h2>
                </div>
                <span className="home-user-name">{user?.fullName || user?.username}</span>
            </div>
            <div className="home-shortcut-grid">
                {shortcuts
                    .filter(({ privilege }) => hasPrivilege(privilege))
                    .map(({ label, mobileLabel, description, to, icon: Icon }) => (
                        <Link
                            aria-label={compact ? label : undefined}
                            className="home-shortcut"
                            key={to}
                            to={to}
                        >
                            <span className="home-shortcut-icon" aria-hidden="true">
                                <Icon />
                            </span>
                            <span className="home-shortcut-copy">
                                <strong className="home-shortcut-label">{label}</strong>
                                <strong className="home-shortcut-label-mobile">{mobileLabel}</strong>
                                <span>{description}</span>
                            </span>
                            <FaArrowRight className="home-shortcut-arrow" aria-hidden="true" />
                        </Link>
                    ))}
            </div>
        </section>
    );
}

function Home() {
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
    const [isCompactLayout, setIsCompactLayout] = useState(() =>
        window.matchMedia(COMPACT_LAYOUT_QUERY).matches
    );
    const [activeCertificateIndex, setActiveCertificateIndex] = useState(1);
    const [certificateSlideDirection, setCertificateSlideDirection] = useState("next");
    const { user, hasPrivilege } = useAuth();
    const { systemName, logoUrl } = useSystem();

    useEffect(() => {
        const mediaQuery = window.matchMedia(COMPACT_LAYOUT_QUERY);
        const handleViewportChange = (event) => setIsCompactLayout(event.matches);

        mediaQuery.addEventListener("change", handleViewportChange);
        return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }, []);

    const previousCertificate = certificatePreviews[activeCertificateIndex - 1];
    const nextCertificate = certificatePreviews[activeCertificateIndex + 1];
    const showPreviousCertificate = () => {
        if (!previousCertificate) return;
        setCertificateSlideDirection("previous");
        setActiveCertificateIndex((index) => index - 1);
    };
    const showNextCertificate = () => {
        if (!nextCertificate) return;
        setCertificateSlideDirection("next");
        setActiveCertificateIndex((index) => index + 1);
    };
    return (
        <DashboardLayout>
            <div className="home-page">
                <header className="home-page-header">
                    <div className="home-brand">
                        {logoUrl ? (
                            <img className="home-brand-logo" src={logoUrl} alt="" />
                        ) : (
                            <span className="home-brand-mark" aria-hidden="true">
                                <FaShieldAlt />
                            </span>
                        )}
                        <div>
                            <p className="home-eyebrow">{systemName}</p>
                            <h1>Certificados digitales</h1>
                            <p className="home-intro">
                                Certificados digitales con interoperabilidad de dependencias.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="home-privacy-link"
                        onClick={() => setIsPrivacyOpen(true)}
                    >
                        <FaLock aria-hidden="true" />
                        Aviso de privacidad
                    </button>
                </header>

                {isCompactLayout && (
                    <HomeShortcuts user={user} hasPrivilege={hasPrivilege} compact />
                )}

                <section className="home-overview" aria-labelledby="home-overview-title">
                    <div className="home-overview-copy">
                        <p className="home-overview-kicker">Plataforma de servicios digitales</p>
                        <h2 id="home-overview-title">
                            Un punto de acceso para ciudadanía, patrimonio y empresas.
                        </h2>
                        <p>
                            Consulta tus servicios y certificados en un espacio conectado con las
                            dependencias participantes.
                        </p>
                    </div>

                    <div className="home-certificate-carousel">
                        <div
                            className="home-network"
                            role="group"
                            aria-roledescription="carrusel"
                            aria-label="Tipos de certificados"
                        >
                            {certificatePreviews.map((preview, index) => (
                                <CertificatePreview
                                    key={preview.type}
                                    {...preview}
                                    isActive={index === activeCertificateIndex}
                                    slideDirection={certificateSlideDirection}
                                />
                            ))}
                        </div>
                        <div className="home-certificate-controls" role="group" aria-label="Paginación de certificados">
                            <button
                                type="button"
                                className="home-certificate-arrow"
                                aria-label={previousCertificate ? `Anterior: ${previousCertificate.title}` : "Primer certificado"}
                                title="Certificado anterior"
                                disabled={!previousCertificate}
                                onClick={showPreviousCertificate}
                            >
                                <FaChevronLeft aria-hidden="true" />
                            </button>
                            <span className="home-certificate-page" aria-live="polite">
                                {activeCertificateIndex + 1} de {certificatePreviews.length}
                            </span>
                            <button
                                type="button"
                                className="home-certificate-arrow"
                                aria-label={nextCertificate ? `Siguiente: ${nextCertificate.title}` : "Último certificado"}
                                title="Certificado siguiente"
                                disabled={!nextCertificate}
                                onClick={showNextCertificate}
                            >
                                <FaChevronRight aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </section>

                {!isCompactLayout && (
                    <HomeShortcuts user={user} hasPrivilege={hasPrivilege} />
                )}
            </div>
            <PrivacyNoticeModal
                isOpen={isPrivacyOpen}
                onClose={() => setIsPrivacyOpen(false)}
            />
        </DashboardLayout>
    );
}

export default Home;